import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiFilter } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getAllOrders, updateOrderStatus } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import './AdminOrders.css';


const STATUS_CONFIG = {
  placed:     { cls: 'badge-info',    label: 'Placed' },
  confirmed:  { cls: 'badge-primary', label: 'Confirmed' },
  processing: { cls: 'badge-warning', label: 'Processing' },
  shipped:    { cls: 'badge-accent',  label: 'Shipped' },
  delivered:  { cls: 'badge-success', label: 'Delivered' },
  cancelled:  { cls: 'badge-danger',  label: 'Cancelled' },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  ...ALL_STATUSES.map((s) => ({ key: s, label: STATUS_CONFIG[s].label })),
];

const NEXT_STATUSES = {
  placed:     ['confirmed', 'cancelled'],
  confirmed:  ['processing', 'cancelled'],
  processing: ['shipped', 'cancelled'],
  shipped:    ['delivered'],
  delivered:  [],
  cancelled:  [],
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]   = useState('');
  const [updating, setUpdating] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, sort: '-createdAt' };
      const { data } = await getAllOrders(params);
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Admin orders error:', err?.response?.data || err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusChange = async (order, newStatus) => {
    setUpdating(order._id);
    try {
      await updateOrderStatus(order._id, { status: newStatus });
      setOrders((prev) => prev.map((o) => o._id === order._id ? { ...o, orderStatus: newStatus } : o));
      toast.success(`Order marked as ${newStatus}`);
    } catch {
      toast.error('Failed to update order status');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = orders.filter((o) => {
    if (filter !== 'all' && o.orderStatus !== filter) return false;
    if (dateFrom) {
      const from = new Date(dateFrom);
      if (new Date(o.createdAt) < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(o.createdAt) > to) return false;
    }
    return true;
  });

  const totalValue = filtered.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <AdminLayout title="All Orders">
      <div className="aorders-page">
        {/* Tabs */}
        <div className="aorders-toolbar">
          <div className="aorders-tabs">
            {FILTER_TABS.map((tab) => {
              const count = tab.key === 'all' ? orders.length : orders.filter((o) => o.orderStatus === tab.key).length;
              return (
                <button
                  key={tab.key}
                  className={`aorders-tab ${filter === tab.key ? 'active' : ''}`}
                  onClick={() => setFilter(tab.key)}
                >
                  {tab.label}
                  {count > 0 && <span className="aorders-tab-count">{count}</span>}
                </button>
              );
            })}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowFilters((v) => !v)}>
            <FiFilter size={13} /> {showFilters ? 'Hide' : 'Date Filter'}
          </button>
        </div>

        {/* Date filters */}
        {showFilters && (
          <div className="aorders-date-filters card">
            <label className="form-label">From</label>
            <input type="date" className="form-input" style={{ width: 'auto' }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <label className="form-label">To</label>
            <input type="date" className="form-input" style={{ width: 'auto' }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <button className="btn btn-ghost btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>
          </div>
        )}

        {/* Summary */}
        <div className="aorders-summary">
          <span className="text-muted text-sm">{filtered.length} order{filtered.length !== 1 ? 's' : ''}</span>
          <span className="font-semi" style={{ fontSize: '0.875rem' }}>
            Total: Rs. {totalValue.toLocaleString('en-PK', { minimumFractionDigits: 2 })}
          </span>
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /><span>Loading orders…</span></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h3>No orders match your filters</h3>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Buyer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Date</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Update</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const { cls } = STATUS_CONFIG[order.orderStatus] || { cls: 'badge-info' };
                  const date = new Date(order.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
                  const nextOpts = NEXT_STATUSES[order.orderStatus] || [];
                  const isUpdating = updating === order._id;

                  return (
                    <tr key={order._id}>
                      <td>
                        <p className="ao-order-num">{order.orderNumber}</p>
                      </td>
                      <td className="font-medium">{order.buyer?.name || '—'}</td>
                      <td className="text-muted text-sm">
                        {(order.items || []).slice(0, 2).map((i) => i.name || i.product?.name || '—').join(', ')}
                        {order.items?.length > 2 && ` +${order.items.length - 2}`}
                      </td>
                      <td className="font-semi">Rs. {Number(order.total).toFixed(2)}</td>
                      <td className="text-muted text-sm">{date}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${cls}`}>{order.orderStatus}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {nextOpts.length > 0 ? (
                          <div className="ao-select-wrap">
                            <select
                              className="ao-status-select"
                              defaultValue=""
                              onChange={(e) => { if (e.target.value) handleStatusChange(order, e.target.value); e.target.value = ''; }}
                              disabled={isUpdating}
                            >
                              <option value="" disabled>Move to…</option>
                              {nextOpts.map((s) => (
                                <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>
                              ))}
                            </select>
                            <FiChevronDown size={12} className="ao-select-icon" />
                          </div>
                        ) : (
                          <span className="text-muted text-sm">—</span>
                        )}
                      </td>
                      <td>
                        <Link to={`/orders/${order._id}`} className="btn btn-ghost btn-sm">View</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
